/**
 * User Service
 *
 * API client for user-related endpoints (profile, avatar).
 */

import { fetchApi } from '@/shared/services/api';
import type { HouseholdRead, UserRead, UserUpdate } from '@/shared/types';

export const userService = {
  /**
   * Get the current user's profile
   */
  getMe: () => fetchApi<UserRead>('/users/me'),

  /**
   * Upload or replace the current user's avatar image.
   * Accepts JPEG, PNG, GIF, or WebP images up to 2MB.
   */
  uploadAvatar: (file: File): Promise<UserRead> => {
    const formData = new FormData();
    formData.append('file', file);

    return fetchApi<UserRead>('/users/me/avatar', {
      method: 'PUT',
      body: formData,
    });
  },

  /**
   * Delete the current user's avatar image.
   */
  deleteAvatar: () =>
    fetchApi<void>('/users/me/avatar', { method: 'DELETE' }),

  /**
   * Update the current user's profile (e.g. avatar_color).
   */
  updateProfile: (data: { avatar_color?: string | null }) =>
    fetchApi<UserRead>('/users/me', { method: 'PATCH', body: data }),

  /**
   * Update any user by ID (owner-only for other users).
   */
  updateUser: (userId: string, data: UserUpdate) =>
    fetchApi<UserRead>(`/users/${userId}`, { method: 'PATCH', body: data }),

  /**
   * Soft-delete a user by ID (owner-only, cannot delete self).
   */
  deleteUser: (userId: string) =>
    fetchApi<void>(`/users/${userId}`, { method: 'DELETE' }),

  enableHouseholdSharing: () =>
    fetchApi<HouseholdRead>('/households/sharing/enable', { method: 'POST' }),

  disableHouseholdSharing: () =>
    fetchApi<HouseholdRead>('/households/sharing/disable', { method: 'POST' }),

  respondToShareRequest: (accept: boolean) =>
    fetchApi<UserRead>('/users/me/net-worth-sharing', {
      method: 'POST',
      body: { accept },
    }),
};
